import React, { useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { CCard, CCardBody, CRow, CCol, CButton, CModal, CModalHeader, CModalTitle, CModalFooter } from '@coreui/react';
import Loading from '../../../components/Loading';
import { formatCurrency, extractErrorMessage } from '../../../helpers';
import _t from 'counterpart';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { deleteFundedAccountType, getFundedAccountType } from '../../../services/BackendService';
import { useAppSelector } from '../../../helpers/customHooks';
import PageLayout from '../../../components/PageLayout';
import { Permission } from '../../../reducers/userReducer';
import Description from '../../../components/Description';
import toast from 'react-hot-toast';
import { FundedAccountBehavior, IFundedAccountType } from './types';
import { IGetFundedAccountTypeResponse } from './types';

const FundedAccountTypePage = () => {
  const [deleteFundedAccountModalShow, deleteFundedAccountModalToggle] = useState<boolean>(false);

  const permissions = useAppSelector((state) => state.user.permissions);

  const params = useParams();
  const history = useHistory();

  const { id } = params as any;

  const { data, isLoading } = useQuery<IGetFundedAccountTypeResponse>(
    ['funded-account', id],
    () => getFundedAccountType(id),
    {
      onError: (e: any) => {
        const error = extractErrorMessage(e);
        toast.error(error);
      },
      retry: false,
    }
  );

  const queryClient = useQueryClient();

  const deleteFundedAccountMutation = useMutation((id: number) => deleteFundedAccountType(id), {
    onSuccess: async () => {
      await queryClient.invalidateQueries('funded-accounts');
      toast.success(_t('funded-account-types.type-deleted-successfully'));
      deleteFundedAccountModalToggle(false);
    },
    onError: (e: any) => {
      const error = extractErrorMessage(e);
      toast.error(error);
    },
    retry: false,
  });

  if (isLoading || !data) {
    return <Loading />;
  }

  const {
    title,
    leverage,
    initialBalance,
    minTradingDays,
    maxInactivityDays,
    maxDailyDrawdownFactor,
    maxTotalDrawdownFactor,
    traderProfitShareFactor,
    accountGroup,
    accountCurrency,
    behavior,
    profitTargetFactor,
    isDemo,
  } = data.fundedAccountType!;
  const deleteFundedAccountConfirmed = () => {
    deleteFundedAccountMutation.mutate(id);
    history.push(`/funded-account-types`);
  };

  const onEditClicked = () => {
    history.push(`/funded-account-types/form?edit=${id}`);
  };

  const hasEditPermissions = permissions?.includes(Permission.MANAGE_FUNDED_ACCOUNT_TYPES);

  return (
    <PageLayout
      title={_t.translate('funded-account-types.account-type') + ` #${id}`}
      titleAppend={
        <>
          {hasEditPermissions && (
            <div className="d-flex justify-content-between float-right mb-2">
              <div className="d-flex flex-nowrap flex-row-reverse">
                {data.canModify && (
                  <CButton color="primary" onClick={onEditClicked}>
                    {_t('action.edit')}
                  </CButton>
                )}
                {data.canDelete && (
                  <CButton color="danger" className="mr-2" onClick={() => deleteFundedAccountModalToggle(true)}>
                    {_t('action.delete')}
                  </CButton>
                )}
              </div>
            </div>
          )}
        </>
      }
    >
      <CRow>
        <CCol md={12}>
          <CCard>
            <CCardBody>
              <CRow>
                <Description title={_t('funded-account-types.id')}>{id}</Description>
                <Description title={_t('funded-account-types.title')}>{title}</Description>
              </CRow>
              <CRow>
                <Description title={_t('funded-account-types.is-demo')}>
                  {isDemo ? _t('global.yes') : _t('global.no')}
                </Description>
                <Description title={`${_t('funded-account-types.initial-balance')}`}>
                  {formatCurrency(initialBalance, accountCurrency)}
                </Description>
              </CRow>
              <CRow>
                <Description title={_t('funded-account-types.leverage')}>{leverage}</Description>
                <Description title={_t('funded-account-types.behavior')}>{behavior}</Description>
              </CRow>
              <CRow>
                <Description title={_t('funded-account-types.min-trading-days')}>{minTradingDays}</Description>
                <Description title={_t('funded-account-types.max-inactivity-days')}>{maxInactivityDays}</Description>
              </CRow>
              <CRow>
                <Description title={_t('funded-account-types.max-daily-drawdown')}>
                  {(Number(maxDailyDrawdownFactor) * 100).toFixed(2)}%
                </Description>
                <Description title={_t('funded-account-types.max-total-drawdown')}>
                  {(Number(maxTotalDrawdownFactor) * 100).toFixed(2)}%
                </Description>
              </CRow>
              <CRow>
                <Description title={_t('funded-account-types.profit-target-factor')}>
                  {behavior === FundedAccountBehavior.BINARY && profitTargetFactor != null
                    ? `${(Number(profitTargetFactor) * 100).toFixed(2)}%`
                    : '—'}
                </Description>
                <Description title={_t('funded-account-types.trader-profit-share-factor')}>
                  {!isDemo && traderProfitShareFactor != null
                    ? `${(Number(traderProfitShareFactor) * 100).toFixed(2)}%`
                    : '—'}
                </Description>
              </CRow>
              <CRow>
                <Description title={_t('funded-account-types.account-currency')}>{accountCurrency}</Description>
                <Description title={_t('funded-account-types.account-group')}>{accountGroup}</Description>
              </CRow>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
      <CModal show={deleteFundedAccountModalShow} onClose={() => deleteFundedAccountModalToggle(false)}>
        <CModalHeader>
          <CModalTitle>{_t('funded-account-types.delete-modal-title')}</CModalTitle>
        </CModalHeader>
        {permissions?.includes(Permission.MANAGE_FUNDED_ACCOUNT_TYPES) && (
          <CModalFooter className="modal-footer--alternative">
            <CButton color="secondary" onClick={() => deleteFundedAccountModalToggle(false)}>
              {_t('action.cancel')}
            </CButton>
            <CButton color="danger" onClick={deleteFundedAccountConfirmed}>
              {_t('funded-account-types.delete')}
            </CButton>
          </CModalFooter>
        )}
      </CModal>
    </PageLayout>
  );
};

export default FundedAccountTypePage;
